/**
 * OS AVISOS DO BACKEND, TRADUZIDOS ANTES DE CHEGAR À TELA — `T1.26`.
 *
 * ── O QUE ESTAVA NO AR, MEDIDO EM PRODUÇÃO EM 26/09/2026 ─────────────────────────────────────
 *
 * Gerando em `/roteirizar` com o crédito do provedor esgotado, a tela mostrava, para a criança:
 *
 *     "a quebra por IA falhou (HTTP 402 {"error":"You have depleted your monthly included
 *      credits. Purchase pre-paid credits to continue using Inference Providers. Alternatively,
 *      subscribe to PRO to get 20x more included usage."}); usei a quebra por regra"
 *
 * Erro HTTP cru, JSON de fornecedor e **propaganda de assinatura**, em inglês, numa plataforma que
 * serve turma de escola. E isso contraria uma decisão medida deste épico — o `T1.26`, *"a tela de
 * configurações expõe informação que o usuário não precisa (IAs, tokens, custo)"* —, que eu validei
 * em 26/09 em quatro telas **e não achei este vazamento**, porque ele só aparece depois de uma falha
 * do provedor. Estado que não se alcança não se mede.
 *
 * ── POR QUE A CORREÇÃO É AQUI, E NÃO SÓ NO BACKEND ──────────────────────────────────────────
 *
 * O backend também deve parar de mandar texto assim — mas consertar só lá deixa a tela **confiando
 * em string de outro serviço**. A próxima mensagem nova vaza igual. A fronteira é este arquivo: o
 * que a criança lê é decidido no frontend, e o que não for reconhecido **não vai para a tela**.
 *
 * ── O QUE SE FAZ COM O DESCONHECIDO, E ISSO É A DECISÃO ─────────────────────────────────────
 *
 * Duas saídas ruins e uma boa. Mostrar cru é o defeito de hoje. Mostrar "ocorreu um erro" para todo
 * aviso apagaria os que são úteis — *"1 cena em 1 página no formato Tirinha"* é informação boa, e a
 * criança tem direito a ela. A saída é **reconhecer o que é útil, traduzir o que é técnico e
 * descartar o resto**, com o original indo para o `console` de quem depura.
 *
 * Descartar perde informação, e é o preço certo: um aviso a menos é uma linha que ninguém leu; um
 * aviso cru a mais é o nome de um fornecedor e um pedido de assinatura na frente de uma criança.
 */

/** O que a tela mostra, e nada além disto. */
export interface AvisoParaCrianca {
  texto: string;
  /**
   * T26.R9 · o quadro de que o aviso fala (0 = o primeiro). Redesenhar esse quadro com sucesso
   * retira o aviso dele — o relatório de 09/10 viu «não ficou pronto» continuar na lista ao lado
   * do quadro já desenhado.
   */
  quadro?: number;
  /** `informacao` = útil e neutro. `ajuste` = algo saiu diferente do ideal, mas a história saiu. */
  tipo: "informacao" | "ajuste";
}

/**
 * Cada regra reconhece uma FORMA de aviso do backend e devolve a frase da criança.
 *
 * A ordem importa: a primeira que casar decide. As mais específicas vêm antes.
 */
const REGRAS: { quando: RegExp; traduz: (m: RegExpMatchArray) => AvisoParaCrianca }[] = [
  // ── T16.3 · O QUE O DESENHO NÃO VIU ──────────────────────────────────────────────────────
  //
  // Estes três chegaram em 03/10 com os avisos novos do backend, e sem eles a tela continuaria
  // CALADA — que é a queixa literal do QA: *"nenhum aviso na tela"*. Um aviso sem tradução é
  // descartado por este arquivo, então aviso novo no backend sem regra nova aqui é aviso que não
  // existe. Vêm antes das regras genéricas porque falam do texto da própria criança.
  {
    // "o desenho do quadro N usa X das Y letras que você escreveu; a legenda mostra o texto inteiro"
    quando: /desenho do quadro\s*(\d+)\s*usa\s*(\d+)\s*das\s*(\d+)\s*letras/i,
    traduz: (m) => ({
      tipo: "ajuste",
      texto:
        `No quadro ${m[1]}, o desenho foi pedido com ${m[2]} das ${m[3]} letras que você escreveu. ` +
        `A legenda mostra a frase inteira; o desenho viu só o começo. Para o desenho ver tudo, ` +
        `quebre essa linha em duas.`,
    }),
  },
  {
    // "em N quadro(s) o desenho usa só parte do texto — no mais cortado, X das Y letras"
    quando: /em\s*(\d+)\s*quadro\(?s?\)?\s*o desenho usa s[óo] parte do texto.*?(\d+)\s*das\s*(\d+)\s*letras/i,
    traduz: (m) => ({
      tipo: "ajuste",
      texto:
        `Em ${m[1]} quadros o desenho foi pedido com só parte da frase — no mais cortado, ` +
        `${m[2]} das ${m[3]} letras. As legendas mostram as frases inteiras. ` +
        `Quebre as linhas mais longas em duas.`,
    }),
  },
  {
    // "a descrição do personagem não caberia no pedido de N quadro(s)"
    quando: /descri[çc][ãa]o do personagem n[ãa]o caberia no pedido de\s*(\d+)\s*quadro/i,
    traduz: (m) => ({
      tipo: "ajuste",
      texto:
        `A descrição do personagem não coube no pedido de ${m[1]} quadro(s), então nesses a figura ` +
        `pode sair diferente. Encurte a descrição, ou encurte a frase da cena.`,
    }),
  },
  {
    // "a quebra por IA partiu uma frase no meio; usei a quebra por regra" — T16.3. A regra geral
    // logo abaixo só reconhece "falhou", e este caso não é falha: é recusa de um corte ruim.
    quando: /partiu uma frase no meio/i,
    traduz: () => ({
      tipo: "ajuste",
      texto:
        "Separei as cenas frase por frase. A separação automática tinha cortado uma frase " +
        "no meio, e meia frase não vira legenda.",
    }),
  },
  {
    // "N cena(s) em M página(s) no formato "X"" — este é útil como está, e é o único que já falava
    // com a pessoa. Reescrito só para sair do plural com parêntese, que ninguém fala.
    quando: /(\d+)\s*cena\(?s?\)?\s*em\s*(\d+)\s*p[áa]gina\(?s?\)?\s*no formato\s*"?([^"]+)"?/i,
    traduz: (m) => {
      const cenas = Number(m[1]);
      const paginas = Number(m[2]);
      const formato = m[3].trim().replace(/\.$/, "");
      return {
        tipo: "informacao",
        texto:
          `Separei a sua história em ${cenas === 1 ? "1 cena" : `${cenas} cenas`}, ` +
          `${paginas === 1 ? "numa página" : `em ${paginas} páginas`}, no formato ${formato}.`,
      };
    },
  },
  {
    // A quebra por IA falhou e a por regra assumiu. A criança não precisa saber QUAL falhou nem por
    // quê: ela precisa saber que a separação foi mais simples, porque isso explica um resultado
    // mais tosco sem culpar o texto dela.
    quando: /quebra por IA falhou|falha.*(intelig[êe]ncia|IA)\b.*quebra/i,
    traduz: () => ({
      tipo: "ajuste",
      texto:
        "Separei as cenas do jeito simples desta vez. Se o planejamento não ficou bom, " +
        "mude a história e planeje de novo — o planejamento aparece antes de desenhar.",
    }),
  },
  {
    // Traducao de cena indisponivel: o desenho sai, e a aderencia cai. Mesma logica.
    quando: /tradu[çc][ãa]o.*(falhou|indispon[íi]vel)|sem tradu[çc][ãa]o/i,
    traduz: () => ({
      tipo: "ajuste",
      texto: "Desenhei direto do seu texto. Alguns detalhes podem não ter aparecido.",
    }),
  },
];

/**
 * Sinais de que um aviso é técnico e NÃO pode ir para a tela nem traduzido — se chegou aqui sem
 * casar com nenhuma regra, ele é descartado de qualquer jeito. Esta lista existe para o TESTE
 * poder cobrar que nenhum deles vaze, e para o `console` dizer o que foi descartado.
 */
export const MARCAS_DE_VAZAMENTO = [
  /HTTP\s*\d{3}/i,
  /\{\s*"?error"?\s*:/i,
  /\btoken(s)?\b/i,
  /\bcredit(s|os)?\b/i,
  /\bsubscribe\b|\bPRO\b|\bpre-?paid\b/i,
  /huggingface|stablehorde|cloudflare|pollinations|openai|replicate/i,
  /\bAPI\b|\bendpoint\b|\bprovider\b/i,
];

/**
 * Traduz a lista do backend para o que a tela pode mostrar.
 *
 * O que não casa com regra nenhuma é **descartado** e registrado no `console` — nunca renderizado.
 */
export function avisosParaCrianca(doBackend: unknown): AvisoParaCrianca[] {
  if (!Array.isArray(doBackend)) return [];
  const saida: AvisoParaCrianca[] = [];

  for (const bruto of doBackend) {
    if (typeof bruto !== "string" || !bruto.trim()) continue;
    const regra = REGRAS.find((r) => r.quando.test(bruto));
    if (!regra) {
      // eslint-disable-next-line no-console
      console.log("[roteirizar] aviso do backend descartado por não ter tradução:", bruto);
      continue;
    }
    const traduzido = regra.traduz(bruto.match(regra.quando) as RegExpMatchArray);

    // Cinto e suspensório: se a tradução por acidente carregar marca técnica, ela não sai.
    if (MARCAS_DE_VAZAMENTO.some((m) => m.test(traduzido.texto))) {
      // eslint-disable-next-line no-console
      console.log("[roteirizar] tradução recusada por conter marca técnica:", traduzido.texto);
      continue;
    }
    // Sem repetir a mesma frase duas vezes: dois avisos do backend podem cair na mesma regra.
    if (!saida.some((a) => a.texto === traduzido.texto)) saida.push(traduzido);
  }

  return saida;
}

/**
 * ── A MENSAGEM DE FALHA, QUE É OUTRO CAMINHO E VAZAVA IGUAL — MEDIDO EM 26/09 ────────────────
 *
 * Tudo acima trata do campo `avisos` do backend. A **falha** tem caminho próprio, e ele estava
 * renderizando `e.message` cru:
 *
 *     setErro(e instanceof Error ? e.message : "Não consegui montar o roteiro.");
 *
 * Medido na vitrine, com o backend fora de alcance: a criança lê **"Failed to fetch"** — inglês,
 * jargão de navegador, numa tela de plataforma escolar. A frase de queda em português existia e era
 * usada só quando o erro NÃO era um `Error`, que é o caso que quase nunca acontece.
 *
 * É o mesmo defeito do `T1.26` por outra porta, e a lição é a de sempre: consertar o caminho que a
 * medição visitou deixa o vizinho. O campo `avisos` passou pela fronteira em 26/09; o campo de erro,
 * que é o que aparece quando algo dá errado de verdade, não.
 *
 * ── POR QUE TRÊS FRASES, E NÃO UMA ──────────────────────────────────────────────────────────
 *
 * "Não deu certo, tente de novo" serve para tudo e não ajuda em nada. As três abaixo distinguem o que
 * a criança pode FAZER: esperar (o desenho está demorando), conferir a internet (não chegou lá), ou
 * chamar quem cuida da plataforma (chegou e voltou errado). A quarta — a barreira de conteúdo — tem
 * tela própria e não passa por aqui.
 */
const FALHAS: { quando: RegExp; frase: string }[] = [
  {
    // `TypeError: Failed to fetch` / `NetworkError` / `Load failed` — o pedido não chegou ao servidor.
    quando: /failed to fetch|networkerror|load failed|err_(connection|network)|net::/i,
    frase:
      "Não consegui falar com o HQ-Lab agora. Confira a internet e tente de novo — a sua história " +
      "continua escrita aqui.",
  },
  {
    // Tempo esgotado: o pedido chegou e a resposta não veio.
    quando: /timeout|timed out|abort|tempo esgotado/i,
    frase:
      "O desenho está demorando mais do que o normal. Espere um pouquinho e tente de novo — a sua " +
      "história continua escrita aqui.",
  },
];

/** A frase que aparece quando nada mais se reconhece. Português, e diz o que fazer. */
export const FALHA_GENERICA =
  "Não consegui montar a sua história agora. Tente de novo em instantes — o que você escreveu " +
  "continua aqui.";

/**
 * A frase de falha que a tela pode mostrar, a partir de qualquer coisa que o `catch` pegou.
 *
 * **Nunca devolve o texto original.** Mesmo quando ele parece inofensivo: "parece inofensivo" foi o
 * julgamento que deixou "Failed to fetch" na tela por três semanas. O original vai para o `console`.
 */
export function falhaParaCrianca(erro: unknown): string {
  const bruto = erro instanceof Error ? erro.message : typeof erro === "string" ? erro : "";
  if (bruto) {
    // eslint-disable-next-line no-console
    console.log("[roteirizar] falha original, que não vai para a tela:", bruto);
  }
  const regra = FALHAS.find((f) => f.quando.test(bruto));
  return regra ? regra.frase : FALHA_GENERICA;
}

/**
 * T26.R7 · OS AVISOS DO CORTE FALAM DO DESENHISTA DE RESERVA. O relatório de 09/10: «a descrição
 * do personagem corta pela metade o que o desenho usa (de 90 para 45 letras)». O desenhista
 * principal (FLUX.2 klein) recebe a cena INTEIRA e a descrição inteira, traduzidas (T24); só o de
 * reserva lê o pedido curto. Avisar de corte na história inteira era avisar de algo que não
 * acontece — o aviso certo vem quando a reserva é usada, quadro por quadro.
 */
export function semOsAvisosDoCorte(avisos: string[]): string[] {
  return avisos.filter(
    (a) => !/o desenho usa s[óo] parte do texto|usa \d+ das \d+ letras que voc[êe] escreveu|descri[çc][ãa]o do personagem n[ãa]o caberia/i.test(a)
  );
}

/** T26.R9 · os avisos que sobram quando o quadro `i` fica pronto. */
export function semOsAvisosDoQuadro(avisos: AvisoParaCrianca[], i: number): AvisoParaCrianca[] {
  return avisos.filter((a) => a.quadro !== i);
}
